import { useState, type JSX } from 'react'
import { Loader2, Trash2, User, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/sonner'
import { ROLES, roleWithinCaps, type Capability, type Role } from '@shared/collaboration/access'
import { useCreateGrant, useDeleteGrant, useGrants, type Grant, type ResourceType } from '../api/use-grants'
import { PrincipalPicker, type PrincipalSelection } from './PrincipalPicker'

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceType: ResourceType
  resourceId: string
  resourceName: string
  capabilities?: Capability[]
}

function roleLabel(role: Role): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

/**
 * Extracts an HTTP status code from whatever useApi() actually throws:
 * a plain `Error('HTTP <code>: ...')` for most non-2xx responses (no
 * `.status`), or a `ForbiddenError` with `.statusCode` (not `.status`) for
 * 403s. Never trust a bare `.status` — it isn't there.
 */
function errorStatusCode(err: unknown): number | undefined {
  const code =
    (err as { statusCode?: number })?.statusCode ??
    (err as { status?: number })?.status ??
    Number(String((err as { message?: string })?.message).match(/^HTTP (\d+)/)?.[1])
  return Number.isNaN(code) ? undefined : code
}

/**
 * "Who has access" dialog for a project or model: lists enriched grants
 * (Task 3's `principal` shape), lets the caller add a new principal at a
 * role within their own capability ceiling (Task 7's PrincipalPicker + the
 * `roleWithinCaps` rule enforced server-side too), and revoke existing
 * grants.
 */
export function ShareDialog({
  open,
  onOpenChange,
  resourceType,
  resourceId,
  resourceName,
  capabilities,
}: ShareDialogProps): JSX.Element {
  const { data: grants, isLoading } = useGrants(resourceType, resourceId)
  const createGrant = useCreateGrant(resourceType, resourceId)
  const deleteGrant = useDeleteGrant(resourceType, resourceId)

  // roleWithinCaps takes a Set<Capability>; the prop arrives as an array.
  const capSet = new Set<Capability>(capabilities ?? [])
  const allowedRoles = ROLES.filter((role): boolean => roleWithinCaps(role, capSet))

  const [pending, setPending] = useState<PrincipalSelection | null>(null)
  const [role, setRole] = useState<Role>(allowedRoles[0] ?? 'viewer')

  const handleShare = (): void => {
    if (!pending) return
    createGrant.mutate(
      { principalType: pending.principalType, principalId: pending.principalId, role },
      {
        onSuccess: (): void => {
          setPending(null)
        },
        onError: (err): void => {
          const code = errorStatusCode(err)
          if (code === 409) {
            toast.error('Already shared with them.')
          } else if (code === 403) {
            toast.error("You can't grant a role above your own.")
          } else {
            toast.error('Failed to share. Please try again.')
          }
          // Dialog stays open — the caller can adjust the principal/role and retry.
        },
      },
    )
  }

  const handleRevoke = (grant: Grant): void => {
    deleteGrant.mutate(grant.id, {
      onError: (): void => {
        toast.error('Failed to revoke access. Please try again.')
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        // The dialog is portaled to <body> in the DOM, but React synthetic
        // events still bubble through the React tree — where this content is a
        // descendant of the <Link>-wrapped cards this dialog opens from. Without
        // this, clicking the search box (or any control) bubbles to the Link and
        // navigates into the model, closing the dialog. Stop it at the root.
        onClick={(e): void => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Share &ldquo;{resourceName}&rdquo;</DialogTitle>
          <DialogDescription>Manage who has access to this {resourceType}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ScrollArea className="max-h-64 rounded-md border">
            <div className="divide-y">
              {isLoading ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : grants && grants.length > 0 ? (
                grants.map((grant): JSX.Element => (
                  <div key={grant.id} className="flex items-center gap-2 px-3 py-2">
                    {grant.principal.type === 'team' ? (
                      <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
                      <span className="truncate text-sm font-medium">{grant.principal.name}</span>
                      {grant.principal.email && (
                        <span className="truncate text-xs text-muted-foreground">{grant.principal.email}</span>
                      )}
                    </span>
                    <Badge variant="secondary">{roleLabel(grant.role)}</Badge>
                    <button
                      type="button"
                      aria-label={`Revoke access for ${grant.principal.name}`}
                      onClick={(): void => handleRevoke(grant)}
                      disabled={deleteGrant.isPending}
                      className="rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="px-3 py-4 text-sm text-muted-foreground">No one else has access yet.</p>
              )}
            </div>
          </ScrollArea>

          <div className="space-y-2 border-t pt-4">
            {pending ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">{pending.label}</span>
                <Button type="button" variant="ghost" size="sm" onClick={(): void => setPending(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <PrincipalPicker onSelect={setPending} />
            )}

            <div className="flex gap-2">
              <Select value={role} onValueChange={(v): void => setRole(v as Role)}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedRoles.map((r): JSX.Element => (
                    <SelectItem key={r} value={r}>
                      {roleLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" onClick={handleShare} disabled={!pending || createGrant.isPending}>
                {createGrant.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Share
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
